// Load-time availability of Pixie, i.e. the shared 24h spend ceiling.
// The Pixie tab is held out of the shop-window list until this confirms the
// assistant is available, so a capped assistant shows no entry point at all —
// no flash of a tab that then vanishes. Global only; the per-visitor throttle
// degrades mid-chat via the `capped` stream frame instead (assistantStream).

let _available = false;
let _resolved = false; // the check has completed (available or not)
let _pending = false; // a fetch is in flight
let _reveal: (() => void) | null = null; // the LATEST caller's reveal callback
// Voice follows the assistant until the check says otherwise. The payload
// carries `voice_availability: false` when the visitor's voice minutes run
// out; absent means this check is not refusing voice.
let _voice = true;

// Specs only — resets the latches so cases stay independent.
export function resetAssistantAvailabilityForTests() {
  _available = false;
  _resolved = false;
  _pending = false;
  _reveal = null;
  _voice = true;
}

// False only when availability has reported voice as used up. The experience
// flag is the other gate; this one is the spend and quota gate.
export function isVoiceAvailable(): boolean {
  return _voice;
}

// True once the load-time check has confirmed the assistant is within its
// global budget. Default false (fail-closed): the tab stays hidden until a
// positive confirmation, and stays hidden if the check never resolves.
export function isAssistantAvailable(): boolean {
  return _available;
}

// Fetch availability once per page load. `onAvailable` is called ONLY when the
// check confirms availability, so the caller can reveal the tab; every other
// outcome — not opted in, capped, non-200, network error, missing url — leaves
// the tab hidden. `fetchImpl` is injectable for tests.
//
// The reveal is re-registered on EVERY call: pluginManager.configure() makes a
// fresh RecipePlugin instance per chain advance, so if the viewer advances while
// the first fetch is still in flight, the NEW instance's reveal must run on
// resolve — never the earlier instance's stale closure (which would append its
// own shopWindowMap[ASK] rather than the live one). Only the first call fetches;
// later ones just swap the callback. Once resolved, `_available` is
// authoritative — `_registerShopWindows` reads it directly, so no reveal is
// needed and the check does not run again.
export function checkAssistantAvailability(
  url: string | null | undefined,
  onAvailable: () => void,
  fetchImpl?: typeof fetch,
) {
  if (_resolved || !url) {
    return;
  }
  _reveal = onAvailable;
  if (_pending) {
    return;
  }
  _pending = true;
  const doFetch = fetchImpl || (typeof fetch !== "undefined" ? fetch : null);
  if (!doFetch) {
    _resolved = true;
    _pending = false;
    return;
  }
  doFetch(url)
    .then(function (response) {
      return response && response.ok ? response.json() : null;
    })
    .then(function (
      body: { available?: unknown; voice_availability?: unknown } | null,
    ) {
      _resolved = true;
      _pending = false;
      if (body && body.voice_availability === false) {
        _voice = false;
      }
      if (body && body.available === true) {
        _available = true;
        if (_reveal) {
          _reveal();
        }
      }
    })
    .catch(function () {
      // fail-closed: leave the tab hidden for this load, and don't retry.
      _resolved = true;
      _pending = false;
    });
}
